import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { InsuranceQuoteCardComponent } from '../../shared/insurance-quote-card/insurance-quote-card.component';
import { HeaderComponent } from '../../shared/header/header.component';
import { StepperComponent } from '../../shared/stepper/stepper.component';
import { QuoteService } from '../../shared/services/quote.service';
import { CompanyService } from '../../shared/services/company.service';
import { QuotaTriggerService } from '../../shared/services/quota-trigger.service';
import {
  QuoteResponse,
  MemberBreakdown,
  QuoteDiscount,
} from '../../models/quote-response.model';
import { LoaderComponent } from '../../components/loader/loader.component';
import { QuoteDetailsModalComponent } from '../../components/quote-details-modal/quote-details-modal.component';

@Component({
  selector: 'app-quotations',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    StepperComponent,
    HeaderComponent,
    LoaderComponent,
    QuoteDetailsModalComponent,
  ],
  templateUrl: './quotations.component.html',
  styleUrls: ['./quotations.component.scss'],
})
export class QuotationsComponent implements OnInit {
  quotes: QuoteResponse[] = [];
  selectedQuote: QuoteResponse | null = null;
  activeStep: number = 4; // Select Quote is step 4
  isLoading: boolean = false;
  error: string | null = null;
  companyName: string = '';
  companyNameArabic: string = '';
  companyId: string = '';
  companyCrNumber: string = '';
  companyPhoneNumber: string = '';
  companyNumberOfEmployee: number = 0;
  crNumber: string | undefined;
  cachedQuotesMap: any[] | undefined;
  showDetailedCoverage: boolean = false;
  showQuoteDetails: boolean = false;
  selectedQuoteForDetails: QuoteResponse | null = null;

  // Sorting properties
  currentSortBy: string = 'requestDate';
  currentSortDirection: string = 'DESC';
  sortOptions = [
    { label: 'Sort by Date (Newest)', value: 'requestDate', direction: 'DESC' },
    { label: 'Sort by Date (Oldest)', value: 'requestDate', direction: 'ASC' },
    { label: 'Sort by Price (Low to High)', value: 'price', direction: 'ASC' },
    { label: 'Sort by Price (High to Low)', value: 'price', direction: 'DESC' },
    {
      label: 'Sort by Provider (A-Z)',
      value: 'providerName',
      direction: 'ASC',
    },
    {
      label: 'Sort by Provider (Z-A)',
      value: 'providerName',
      direction: 'DESC',
    },
    { label: 'Sort by Status', value: 'status', direction: 'ASC' },
  ];

  constructor(
    private companyService: CompanyService,
    private router: Router,
    private quoteService: QuoteService,
    private toastr: ToastrService,
    private quotaTriggerService: QuotaTriggerService
  ) {}

  ngOnInit() {
    this.companyId =
      localStorage.getItem('companyId') ||
      this.router.routerState.snapshot.root.queryParams['companyId'] ||
      '';

    console.log(
      'QuotationsComponent - ngOnInit called for companyId:',
      this.companyId
    );

    const companyIdStr = this.companyId?.toString() || '';
    const shouldRefreshQuota =
      this.quotaTriggerService.shouldRefreshQuota(companyIdStr);

    if (shouldRefreshQuota) {
      this.quotaTriggerService.clearUpdateFlags(companyIdStr);
      console.log(
        'Health declaration or insurance class updated - sending fresh quota request'
      );
      this.sendQuoteRequest();
    } else {
      console.log('No updates detected - checking for cached quotes');
      this.cachedQuotesMap = this.quoteService.getCachedQuotes(this.companyId);
      if (this.cachedQuotesMap) {
        console.log('Using cached quotes');
        this.quotes = this.cachedQuotesMap;
      } else {
        this.sendQuoteRequest();
      }
    }

    this.crNumber = this.companyService.getCompanyDetails()?.crNumber;
  }

  onSortChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const selectedOption = this.sortOptions.find(
      (option) => `${option.value}-${option.direction}` === target.value
    );

    if (selectedOption) {
      this.currentSortBy = selectedOption.value;
      this.currentSortDirection = selectedOption.direction;
      this.fetchQuotes();
    }
  }

  getCurrentSortLabel(): string {
    const currentOption = this.sortOptions.find(
      (option) =>
        option.value === this.currentSortBy &&
        option.direction === this.currentSortDirection
    );
    return currentOption ? currentOption.label : 'Sort by Date (Newest)';
  }

  getProviderInitials(providerName: string): string {
    const words = providerName.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return providerName.substring(0, 2).toUpperCase();
  }

  getProviderColor(providerName: string): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < providerName.length; i++) {
      hash = providerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash % colors.length);
    return colors[colorIndex];
  }

  getMonthlyPremium(
    totalPremium: number,
    employeeCount: number,
    quote: QuoteResponse
  ): string {
    // Use policyTaxablePremium if available, otherwise fall back to totalPremium
    const premiumToUse = quote.policyTaxablePremium || totalPremium;
    let monthlyPremium = premiumToUse / employeeCount;
    return this.formatCurrency(monthlyPremium, quote.currency);
  }

  getCoverageFeatures(quote: QuoteResponse): string[] {
    const features: string[] = [];
    features.push(
      'Comprehensive Coverage',
      'Emergency Services',
      '24/7 Support',
      'Prescription Coverage'
    );
    return features;
  }

  selectQuote(quote: QuoteResponse) {
    this.selectedQuote = quote;
    this.companyService.setSelectedQuote(quote.providerName);
    this.showDetailedCoverage = false;

    // Scroll to the summary section
    setTimeout(() => {
      const summaryElement = document.getElementById('quote-summary');
      if (summaryElement) {
        summaryElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  }

  toggleDetailedCoverage() {
    this.showDetailedCoverage = !this.showDetailedCoverage;
  }

  navigateToHealthDeclaration() {
    window.history.back();
  }

  continueToPayment() {
    if (!this.selectedQuote) {
      this.toastr.warning('Please select a quote to continue.');
      return;
    }

    if (this.selectedQuote.status !== 'COMPLETED') {
      this.toastr.warning('This quote is not ready for purchase yet.');
      return;
    }

    this.verifyAndProceedToPayment(this.selectedQuote);
  }

  verifyAndProceedToPayment(quote: QuoteResponse) {
    const body = {
      companyId: this.companyId,
      quotaReferenceNo: quote.quoteRequestReferenceNo,
    };

    this.isLoading = true;

    this.quoteService
      .verifyQuote(body)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.status === 'SUCCESS') {
            this.companyService.markStepCompleted(4);
            this.router.navigate(['/payment'], {
              queryParams: {
                quotaReferenceNo: quote.quoteRequestReferenceNo,
                quotaresponceId: quote.quoteResponseId,
              },
            });
          } else {
            this.toastr.error('Quote verification failed. Please try again.');
          }
        },
        error: (error) => {
          console.error('Error during quote verification:', error);
          this.toastr.error('Failed to verify the quote. Please try again.');
        },
      });
  }

  sendQuoteRequest() {
    this.isLoading = true;
    this.error = null;

    if (this.companyId !== undefined && this.companyId !== null) {
      this.quoteService
        .sendQuote(this.companyId.toString())
        .pipe(
          catchError((error) => {
            this.toastr.error(
              'Failed to send quote request. Please try again later.'
            );
            this.error =
              'Failed to send quote request. Please try again later.';
            console.error('Error sending quote request:', error);
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((response) => {
          if (response) {
            this.fetchQuotes();
            console.log('Quote request sent successfully:', response);
          }
        });
    } else {
      this.error = 'Company ID is not available. Cannot send quote request.';
      this.isLoading = false;
    }
  }

  openQuoteDetails(quote: QuoteResponse) {
    this.selectedQuoteForDetails = quote;
    this.showQuoteDetails = true;
  }

  closeQuoteDetails() {
    this.showQuoteDetails = false;
    this.selectedQuoteForDetails = null;
  }

  fetchQuotes() {
    this.isLoading = true;
    this.error = null;
    const timeRange = '48h';

    if (this.companyId !== undefined && this.companyId !== null) {
      this.quoteService
        .getQuotes(
          this.companyId.toString(),
          timeRange,
          this.currentSortBy,
          this.currentSortDirection
        )
        .pipe(
          catchError((error) => {
            this.error = 'Failed to load quotes. Please try again later.';
            console.error('Error fetching quotes:', error);
            return of([]);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((quotes) => {
          this.quotes = quotes;

          if (this.companyId !== undefined) {
            this.quoteService.setCachedQuotes(this.companyId, quotes);
          }
        });
    } else {
      this.error = 'Company ID is not available. Cannot fetch quotes.';
      this.isLoading = false;
    }
  }

  refreshQuotes() {
    if (this.companyId !== undefined) {
      this.quoteService.setCachedQuotes(this.companyId, []);
    }
    this.fetchQuotes();
  }

  formatCurrency(amount: number | null, currency: string | null): string {
    if (amount === null || amount === undefined) return 'Not available';
    return `${amount.toLocaleString()} ${currency || 'SAR'}`;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getTotalDiscount(quote: QuoteResponse): number {
    if (!quote.discounts || quote.discounts.length === 0) return 0;
    return quote.discounts.reduce((sum, discount) => {
      return sum + parseFloat(discount.DiscountAmount || '0');
    }, 0);
  }

  getDiscountPercentage(quote: QuoteResponse): string {
    if (!quote.discounts || quote.discounts.length === 0) return '0';
    const totalPercentage = quote.discounts.reduce((sum, discount) => {
      return sum + parseFloat(discount.DiscountPercentage || '0');
    }, 0);
    return totalPercentage.toFixed(0);
  }

  parseFloat(arg0: string): number | null {
    return Number.parseFloat(arg0);
  }
}
